import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Download,
  ChevronDown,
  ChevronRight,
  Search,
  ArrowUpDown,
  Trophy,
  ClipboardList,
  CheckCircle2,
} from 'lucide-react';
import { api, type ApiError } from '../api';
import type { PresentationType } from '../../shared/types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { cn } from '../lib/utils';

interface Year {
  id: number;
  label: string;
  is_current: boolean;
}
interface JudgeScore {
  judgeId: string;
  judgeName: string;
  totalScore: number;
  maxScore: number;
  comments: string;
}
interface ResultRow {
  id: string;
  code?: string;
  type: PresentationType;
  title: string;
  presenterName: string;
  category: string | null;
  allocatedCount: number;
  scoredCount: number;
  averageTotal: number | null;
  maxScore: number | null;
  judgeScores: JudgeScore[];
}

type Filter = 'all' | 'oral' | 'poster';
type SortKey = 'avg' | 'scored' | 'code';
const byCode = (a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export default function Results() {
  const [years, setYears] = useState<Year[]>([]);
  const [yearId, setYearId] = useState<number | null>(null);
  const [rows, setRows] = useState<ResultRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('code');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  // Load the list of years and default to the current one.
  useEffect(() => {
    api<{ years: Year[] }>('admin-years')
      .then((d) => {
        setYears(d.years);
        const current = d.years.find((y) => y.is_current) ?? d.years[0];
        setYearId(current?.id ?? null);
      })
      .catch((e) => setError((e as ApiError).message));
  }, []);

  // Load results for the selected year.
  useEffect(() => {
    if (yearId == null) return;
    setLoading(true);
    api<{ rows: ResultRow[] }>(`results?yearId=${yearId}`)
      .then((d) => setRows(d.rows))
      .catch((e) => setError((e as ApiError).message))
      .finally(() => setLoading(false));
  }, [yearId]);

  const totals = useMemo(() => {
    const scored = rows.reduce((a, r) => a + r.scoredCount, 0);
    const allocated = rows.reduce((a, r) => a + r.allocatedCount, 0);
    const fully = rows.filter((r) => r.allocatedCount > 0 && r.scoredCount >= r.allocatedCount).length;
    return { scored, allocated, fully, presentations: rows.length };
  }, [rows]);

  const view = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows.filter((r) => filter === 'all' || r.type === filter);
    if (q)
      list = list.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.presenterName.toLowerCase().includes(q) ||
          (r.code ?? r.id).toLowerCase().includes(q),
      );
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortKey === 'code') return byCode(a.code ?? a.id, b.code ?? b.id) * dir;
      if (sortKey === 'scored') return (a.scoredCount - b.scoredCount) * dir;
      return ((a.averageTotal ?? -1) - (b.averageTotal ?? -1)) * dir;
    });
  }, [rows, filter, query, sortKey, sortDir]);

  function sortBy(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'code' ? 'asc' : 'desc');
    }
  }

  async function downloadCsv() {
    setDownloading(true);
    try {
      const token = localStorage.getItem('aadpa_token');
      const res = await fetch(`/api/results?format=csv&yearId=${yearId}`, {
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'aadpa-scores.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <main className="mx-auto max-w-6xl space-y-6 px-4 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold tracking-tight">Results &amp; Rankings</h1>
          <select
            value={yearId ?? ''}
            onChange={(e) => setYearId(Number(e.target.value))}
            className="h-9 rounded-md border border-input bg-card px-2 text-sm font-medium shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {years.map((y) => (
              <option key={y.id} value={y.id}>
                {y.label}
                {y.is_current ? ' • current' : ''}
              </option>
            ))}
          </select>
        </div>
        <Button onClick={downloadCsv} disabled={downloading || rows.length === 0}>
          <Download />
          {downloading ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <SummaryCard icon={<ClipboardList className="size-4" />} label="Presentations" value={totals.presentations} />
        <SummaryCard
          icon={<CheckCircle2 className="size-4" />}
          label="Scores submitted"
          value={`${totals.scored}/${totals.allocated}`}
        />
        <SummaryCard icon={<Trophy className="size-4" />} label="Fully scored" value={totals.fully} />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search title, presenter or ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-1.5">
          {(['all', 'oral', 'poster'] as Filter[]).map((f) => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : f === 'oral' ? 'Oral' : 'Posters'}
            </Button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm font-medium text-destructive">{error}</p>}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : (
        <Card className="overflow-hidden">
          <div className="grid grid-cols-[3rem_1fr_5rem_5rem_2rem] items-center gap-2 border-b bg-muted/40 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid-cols-[3rem_1fr_6rem_6rem_6rem_2rem]">
            <SortHeader label="Code" active={sortKey === 'code'} dir={sortDir} onClick={() => sortBy('code')} />
            <span>Presentation</span>
            <span className="hidden sm:block">Type</span>
            <SortHeader
              label="Scored"
              active={sortKey === 'scored'}
              dir={sortDir}
              onClick={() => sortBy('scored')}
              className="justify-center"
            />
            <SortHeader
              label="Avg"
              active={sortKey === 'avg'}
              dir={sortDir}
              onClick={() => sortBy('avg')}
              className="justify-end"
            />
            <span />
          </div>

          {view.length === 0 ? (
            <p className="p-8 text-center text-muted-foreground">No presentations match your filters.</p>
          ) : (
            view.map((r) => {
              const open = expanded === r.id;
              return (
                <div key={r.id} className="border-b last:border-b-0">
                  <button
                    onClick={() => setExpanded(open ? null : r.id)}
                    className="grid w-full grid-cols-[3rem_1fr_5rem_5rem_2rem] items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-accent sm:grid-cols-[3rem_1fr_6rem_6rem_6rem_2rem]"
                  >
                    <span className="text-sm font-bold tabular-nums text-muted-foreground">{r.code ?? r.id}</span>
                    <span className="min-w-0">
                      {r.category && (
                        <Badge variant="secondary" className="mb-0.5 hidden text-[10px] md:inline-flex">
                          {r.category}
                        </Badge>
                      )}
                      <span className="line-clamp-1 text-sm font-medium">{r.title}</span>
                      <span className="line-clamp-1 text-xs text-muted-foreground">{r.presenterName}</span>
                    </span>
                    <span className="hidden sm:block">
                      <Badge variant={r.type === 'oral' ? 'oral' : 'poster'} className="capitalize">{r.type}</Badge>
                    </span>
                    <span className="text-center text-sm tabular-nums text-muted-foreground">
                      {r.scoredCount}/{r.allocatedCount}
                    </span>
                    <span className="text-right tabular-nums">
                      {r.averageTotal != null ? (
                        <span className="font-bold">{r.averageTotal}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </span>
                    <span className="flex justify-center text-muted-foreground">
                      {r.judgeScores.length > 0 &&
                        (open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />)}
                    </span>
                  </button>

                  {open && r.judgeScores.length > 0 && (
                    <div className="space-y-1.5 bg-muted/30 px-4 py-3 pl-14">
                      {r.judgeScores.map((s) => (
                        <div key={s.judgeId} className="flex items-baseline gap-3 text-sm">
                          <span className="w-40 shrink-0 font-medium">{s.judgeName}</span>
                          <span className="font-bold tabular-nums">
                            {s.totalScore}
                            <span className="font-normal text-muted-foreground">/{s.maxScore}</span>
                          </span>
                          {s.comments && <span className="text-muted-foreground">“{s.comments}”</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Card>
      )}
    </main>
  );
}

function SummaryCard({ icon, label, value }: { icon: ReactNode; label: string; value: string | number }) {
  return (
    <Card className="flex items-center gap-3 p-4">
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        {icon}
      </span>
      <div className="min-w-0">
        <div className="truncate text-xl font-bold tabular-nums">{value}</div>
        <div className="truncate text-xs text-muted-foreground">{label}</div>
      </div>
    </Card>
  );
}

function SortHeader({
  label,
  active,
  dir,
  onClick,
  className,
}: {
  label: string;
  active: boolean;
  dir: 'asc' | 'desc';
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-1 text-xs font-semibold uppercase tracking-wide transition-colors hover:text-foreground',
        active ? 'text-foreground' : 'text-muted-foreground',
        className,
      )}
    >
      {label}
      <ArrowUpDown className={cn('size-3', active && dir === 'asc' && 'rotate-180')} />
    </button>
  );
}
